import { channelsData } from './channels.js';
import { layoutData } from './layout.js';

export type ChannelColumn = {
  section: string;
  key: string;
  hardware: string;
  name: string;
  quantity: string;
  unit: string;
  range: number;
  offset: number;
  decimals: number;
  source: string;
  scale: string;
  enabled: boolean;
  note: string;
};

export type OverlayKind =
  | 'sensor_value'
  | 'level_gauge'
  | 'device_icon'
  | 'camera'
  | 'legend'
  | 'clock'
  | 'deprecated_pressure'
  | 'legacy_unused';

export type Overlay = {
  control: string;
  x: number;
  y: number;
  w: number;
  h: number;
  norm_x: number;
  norm_y: number;
  norm_w: number;
  norm_h: number;
  text_static: string;
  kind: OverlayKind;
  channel_key: string | null;
  channel_hardware: string | null;
  data_array?: string;
  data_index?: number;
  unit?: string | null;
  semantics?: string;
};

export type ChannelsFile = {
  schema_version: number;
  total_columns: number;
  columns: ChannelColumn[];
};

export type LayoutFile = {
  page: string;
  background: {
    file: string;
    original_px: [number, number];
    control_px: [number, number];
    control_origin_in_tabpage: [number, number];
    sizemode: string;
  };
  overlays: Overlay[];
};

export const channels = channelsData as unknown as ChannelsFile;
export const layout = layoutData as unknown as LayoutFile;

export const STAGE = { width: 1911, height: 917, offsetX: 24, offsetY: 32 } as const;

export const channelByKey = new Map(channels.columns.map((c) => [c.key, c]));
export const sensorChannels = channels.columns.filter(
  (c) => !['seq', 'ts', 'op'].includes(c.key),
);

export const sensorOverlays = layout.overlays.filter((o) => o.kind === 'sensor_value');
export const levelOverlays = layout.overlays.filter((o) => o.kind === 'level_gauge');
export const iconOverlays = layout.overlays.filter((o) => o.kind === 'device_icon');
export const cameraOverlays = layout.overlays.filter((o) => o.kind === 'camera');
export const legendOverlays = layout.overlays.filter((o) => o.kind === 'legend');
export const clockOverlay = layout.overlays.find((o) => o.kind === 'clock');

export function stageBox(o: Pick<Overlay, 'x' | 'y' | 'w' | 'h'>) {
  return {
    left: o.x + STAGE.offsetX,
    top: o.y + STAGE.offsetY,
    width: o.w,
    height: o.h,
  };
}

export type DeviceIcon = {
  control: string;
  label: string;
  image: string;
  register: 'dev0' | 'dev1';
  bit: number;
};

export const DEVICE_ICONS: Record<string, DeviceIcon> = {
  AirPic: { control: 'AirPic', label: '鼓风机', image: 'AIR_ON.png', register: 'dev0', bit: 2 },
  PoorPumpPic: { control: 'PoorPumpPic', label: '贫液泵', image: 'pump_1.png', register: 'dev0', bit: 0 },
  RichPumpPic: { control: 'RichPumpPic', label: '富液泵', image: 'pump_2.png', register: 'dev0', bit: 1 },
  TowerFanPic: { control: 'TowerFanPic', label: '塔风机', image: 'TowerFan.png', register: 'dev0', bit: 3 },
  TowerPumpPic: { control: 'TowerPumpPic', label: '塔水泵', image: 'pumpOpen2.png', register: 'dev0', bit: 4 },
  LoopPumpPic: { control: 'LoopPumpPic', label: '循环水泵', image: 'pump3.png', register: 'dev0', bit: 5 },
  RefluxPumpPic1: { control: 'RefluxPumpPic1', label: '吸收塔回流泵', image: 'pumpOpen2.png', register: 'dev1', bit: 4 },
  RefluxPumpPic2: { control: 'RefluxPumpPic2', label: '解吸塔回流泵', image: 'pumpOpen.png', register: 'dev1', bit: 5 },
};

export type DeviceState = Record<string, boolean>;

export function decodeDevices(dev0: unknown, dev1: unknown): DeviceState {
  const a = Number(dev0) || 0;
  const b = Number(dev1) || 0;
  const out: DeviceState = {};
  for (const icon of Object.values(DEVICE_ICONS)) {
    const value = icon.register === 'dev0' ? a : b;
    out[icon.control] = Boolean(value & (1 << icon.bit));
  }
  return out;
}

export const OPERATIONS: Record<string, string> = {
  '1': '塔风机 启动',
  '2': '塔风机 停止',
  '3': '塔水泵 启动',
  '4': '塔水泵 停止',
  '5': '循环水泵 启动',
  '6': '循环水泵 停止',
  '7': '解吸塔回流泵 启动',
  '8': '解吸塔回流泵 停止',
  '9': '吸收塔回流泵 启动',
  '10': '吸收塔回流泵 停止',
  '11': '贫液泵 启动（频率控制模式）',
  '12': '贫液泵 启动（PID 模式）',
  '13': '贫液泵 停止',
  '14': '富液泵 启动',
  '15': '富液泵 停止',
  '16': '鼓风机 启动（频率控制模式）',
  '17': '鼓风机 启动（PID 模式）',
  '18': '鼓风机 停止',
};

export function parseOpCodes(op: unknown): string[] {
  if (typeof op !== 'string' || op.trim() === '') return [];
  return op.split('、').map((s) => s.trim()).filter(Boolean);
}

export function formatValue(value: unknown, channel?: ChannelColumn): string {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '';
  const decimals = channel?.decimals ?? 2;
  return n.toFixed(decimals);
}

export function levelPercent(value: unknown, channel?: ChannelColumn): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || !channel || !channel.range) return null;
  return Math.max(0, Math.min(100, (n / channel.range) * 100));
}